/**
 * Community Garden — Unified Toast Manager (GARDEN-140)
 *
 * Single consistent toast/notification system for all garden events.
 * Replaces: toasts.ts, toast-manager.ts, and ad-hoc toast implementations.
 *
 * Features:
 *  - Variants: success, error, warning, info
 *  - Queue management (max 3 visible, dismiss oldest)
 *  - Accessibility: ARIA live regions, keyboard dismiss
 *  - Mobile-friendly positioning
 *  - Deduplication for activity events
 */

import { gardenIcon, escapeHtml } from './garden-icons.js';
import { formatPoints } from './points-store.js';

/* ── Types ───────────────────────────────────────────── */

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  /** Toast message text */
  message: string;
  /** Visual variant (default: 'info') */
  variant?: ToastVariant;
  /** Auto-dismiss duration in ms. Set to 0 for persistent (default: 3000) */
  duration?: number;
  /** Show loading spinner */
  loading?: boolean;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Optional icon override (garden icon name) */
  icon?: string;
  /** Deduplication key (prevents duplicate toasts with same key) */
  dedupeKey?: string;
  /** Deduplication window in ms (default: 2500) */
  dedupeWindow?: number;
}

interface Toast {
  id: string;
  element: HTMLDivElement;
  timeout: ReturnType<typeof setTimeout> | null;
  createdAt: number;
}

interface DedupeEntry {
  key: string;
  timestamp: number;
}

/* ── State ───────────────────────────────────────────── */

let container: HTMLDivElement | null = null;
const queue: Toast[] = [];
const dedupeCache: DedupeEntry[] = [];
let toastIdCounter = 0;
let toastPositionListenersBound = false;

// Constants
const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION = 3000;
const DEFAULT_DEDUPE_WINDOW = 2500;

/* ── Container Setup ─────────────────────────────────── */

function ensureContainer(): HTMLDivElement {
  if (container && document.body.contains(container)) {
    positionToastContainer();
    return container;
  }

  container = document.createElement('div');
  container.id = 'toast-manager-container';
  container.setAttribute('role', 'region');
  container.setAttribute('aria-label', 'Notifications');
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-atomic', 'true');
  
  document.body.appendChild(container);
  positionToastContainer();
  if (!toastPositionListenersBound) {
    window.addEventListener('resize', positionToastContainer);
    window.addEventListener('orientationchange', positionToastContainer);
    toastPositionListenersBound = true;
  }
  return container;
}

function positionToastContainer(): void {
  if (!container) return;
  const isMobileGarden = document.body.classList.contains('garden-view')
    && window.matchMedia('(max-width: 768px)').matches
    && !document.body.classList.contains('garden-sheet-open');

  if (!isMobileGarden) {
    container.style.removeProperty('bottom');
    return;
  }

  const hud = document.getElementById('hud');
  const hudTop = hud?.getBoundingClientRect().top ?? 0;
  const offset = hudTop > 0 && hudTop < window.innerHeight
    ? Math.ceil(window.innerHeight - hudTop + 8)
    : 80;
  container.style.bottom = `${offset}px`;
}

/* ── Deduplication Logic ─────────────────────────────── */

function isDuplicate(key: string, windowMs: number): boolean {
  const now = Date.now();
  
  // Prune expired entries
  while (dedupeCache.length > 0 && now - dedupeCache[0]!.timestamp > windowMs) {
    dedupeCache.shift();
  }
  
  // Check for match
  if (dedupeCache.some((e) => e.key === key)) {
    return true;
  }
  
  // Record this entry
  dedupeCache.push({ key, timestamp: now });
  return false;
}

/* ── Icon Mapping ────────────────────────────────────── */

const VARIANT_ICONS: Record<ToastVariant, string> = {
  success: 'checkmark',
  error: 'info',
  warning: 'lightning',
  info: 'info',
};

/* ── Toast Creation ──────────────────────────────────── */

function createToastElement(options: ToastOptions): HTMLDivElement {
  const toast = document.createElement('div');
  const variant = options.variant ?? 'info';
  const iconName = options.icon ?? VARIANT_ICONS[variant];
  toast.className = `toast toast--${variant}`;
  if (options.duration === 0) toast.classList.add('toast--persistent');
  if (options.action) toast.classList.add('toast--actionable');
  
  // Build icon HTML
  const iconHtml = `<span class="toast-icon">${gardenIcon(iconName, 16)}</span>`;
  
  // Build spinner HTML
  const spinnerHtml = options.loading
    ? '<span class="toast-spinner" aria-hidden="true"></span>'
    : '';
  
  // Build content
  const contentHtml = `<span class="toast-message">${escapeHtml(options.message)}</span>`;
  
  // Assemble toast
  toast.innerHTML = `${spinnerHtml || iconHtml}${contentHtml}`;
  
  // Add close button for persistent toasts or action toasts
  if (options.duration === 0 || options.action) {
    const closeBtn = document.createElement('button');
    closeBtn.className = 'toast-close';
    closeBtn.dataset.toastClose = '';
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', 'Dismiss notification');
    toast.appendChild(closeBtn);
  }
  
  // Add action button if provided
  if (options.action) {
    const btn = document.createElement('button');
    btn.className = 'toast-action';
    btn.textContent = options.action.label;
    btn.onclick = (e) => {
      e.stopPropagation();
      options.action!.onClick();
    };
    toast.appendChild(btn);
  }
  
  return toast;
}

/* ── Core Toast Functions ───────────────────────────── */

/**
 * Show a toast notification
 */
export function showToast(options: ToastOptions | string): void {
  const opts: ToastOptions = typeof options === 'string' ? { message: options } : options;
  
  // Check deduplication
  if (opts.dedupeKey) {
    const windowMs = opts.dedupeWindow ?? DEFAULT_DEDUPE_WINDOW;
    if (isDuplicate(opts.dedupeKey, windowMs)) {
      return;
    }
  }
  
  const wrapper = ensureContainer();
  
  // Create toast element
  const toastElement = createToastElement(opts);
  const toastId = `toast-${++toastIdCounter}`;
  toastElement.id = toastId;
  toastElement.querySelector<HTMLElement>('[data-toast-close]')?.addEventListener('click', (event) => {
    event.stopPropagation();
    dismissToast(toastId);
  });
  
  // Add keyboard dismiss
  toastElement.setAttribute('tabindex', '0');
  toastElement.setAttribute('role', 'alert');
  toastElement.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      dismissToast(toastId);
    }
  });
  
  // Click to dismiss (unless persistent with action)
  if (opts.duration !== 0 || !opts.action) {
    toastElement.addEventListener('click', () => {
      dismissToast(toastId);
    });
  }
  
  // Add to DOM
  wrapper.appendChild(toastElement);
  
  // Trigger enter animation
  requestAnimationFrame(() => {
    toastElement.classList.add('toast--visible');
  });
  
  // Set up auto-dismiss timeout
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const duration = opts.duration ?? DEFAULT_DURATION;
  
  if (duration > 0) {
    timeout = setTimeout(() => {
      dismissToast(toastId);
    }, duration);
  }
  
  // Add to queue
  const toast: Toast = {
    id: toastId,
    element: toastElement,
    timeout,
    createdAt: Date.now(),
  };
  queue.push(toast);
  
  // Enforce max visible toasts
  while (queue.length > MAX_VISIBLE_TOASTS) {
    const oldest = queue.shift();
    if (oldest) {
      removeToastElement(oldest);
    }
  }
}

/**
 * Dismiss a specific toast by ID
 */
export function dismissToast(toastId: string): void {
  const index = queue.findIndex((t) => t.id === toastId);
  if (index === -1) return;
  
  const toast = queue.splice(index, 1)[0];
  if (toast) {
    removeToastElement(toast);
  }
}

/**
 * Remove a toast element with animation
 */
function removeToastElement(toast: Toast): void {
  if (toast.timeout) {
    clearTimeout(toast.timeout);
  }
  
  const element = toast.element;
  element.classList.remove('toast--visible');
  element.classList.add('toast--exiting');
  
  element.addEventListener('transitionend', () => {
    element.remove();
  }, { once: true });
  
  // Safety cleanup
  setTimeout(() => {
    if (element.parentNode) {
      element.remove();
    }
  }, 350);
}

/**
 * Dismiss all visible toasts
 */
export function dismissAllToasts(): void {
  while (queue.length > 0) {
    const toast = queue.shift();
    if (toast) {
      removeToastElement(toast);
    }
  }
}

/**
 * Update an existing toast (for progress/loading states)
 */
export function updateToast(toastId: string, options: Partial<ToastOptions>): boolean {
  const toast = queue.find((t) => t.id === toastId);
  if (!toast) return false;
  
  // Update message if provided
  if (options.message) {
    const msgSpan = toast.element.querySelector('.toast-message');
    if (msgSpan) {
      msgSpan.textContent = options.message;
    }
  }
  
  // Update loading state
  if (options.loading !== undefined) {
    const existingSpinner = toast.element.querySelector('.toast-spinner');
    if (options.loading && !existingSpinner) {
      const spinner = document.createElement('span');
      spinner.className = 'toast-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      toast.element.insertBefore(spinner, toast.element.firstChild);
    } else if (!options.loading && existingSpinner) {
      existingSpinner.remove();
    }
  }

  if (options.variant) {
    toast.element.classList.remove('toast--success', 'toast--error', 'toast--warning', 'toast--info');
    toast.element.classList.add(`toast--${options.variant}`);
  }
  
  // Update duration/timeout
  if (options.duration !== undefined && toast.timeout) {
    clearTimeout(toast.timeout);
    toast.timeout = null;
    
    if (options.duration > 0) {
      toast.timeout = setTimeout(() => {
        dismissToast(toast.id);
      }, options.duration);
    }
  }
  
  return true;
}

/* ── Convenience Methods ─────────────────────────────── */

/** Show a success toast */
export function showSuccess(message: string, duration?: number): void {
  showToast({ message, variant: 'success', duration });
}

/** Show an error toast */
export function showError(message: string, duration?: number): void {
  showToast({ message, variant: 'error', duration: duration ?? 5000 });
}

/** Show a warning toast */
export function showWarning(message: string, duration?: number): void {
  showToast({ message, variant: 'warning', duration });
}

/** Show an info toast */
export function showInfo(message: string, duration?: number): void {
  showToast({ message, variant: 'info', duration });
}

/** Show a loading toast (persistent, call dismissToast to remove) */
export function showLoading(message: string): string {
  const id = `toast-${++toastIdCounter}`;
  showToast({ message, variant: 'info', loading: true, duration: 0, dedupeKey: message });
  return id;
}

/* ── GARDEN-138: Optimistic Update Rollback Notifications ───────────── */

/**
 * Show a rollback notification when an optimistic update fails
 * GARDEN-138: Provides user feedback for failed optimistic operations
 */
export function showRollbackToast(
  message: string,
  operationType?: 'points' | 'avatar' | 'petFeed' | 'theme' | 'generic'
): void {
  // Map operation types to user-friendly messages
  const defaultMessages: Record<string, string> = {
    points: 'Points update failed. Reverting changes.',
    avatar: 'Avatar change failed. Reverted to previous look.',
    petFeed: 'Failed to feed companion. Please try again.',
    theme: 'Theme change failed. Reverting to previous theme.',
    generic: 'Action failed. Changes have been reverted.',
  };

  const displayMessage = message || defaultMessages[operationType || 'generic'];

  showToast({
    message: displayMessage,
    variant: 'error',
    duration: 4000,
    dedupeKey: `rollback:${operationType || 'generic'}`,
    dedupeWindow: 3000,
    icon: 'warning',
  });
}

/**
 * Show a pending state indicator for optimistic operations
 */
export function showPendingToast(
  message: string,
  operationId: string
): string {
  showToast({
    message,
    variant: 'info',
    loading: true,
    duration: 0, // Persistent until dismissed
    dedupeKey: `pending:${operationId}`,
  });
  return operationId;
}

/**
 * Dismiss a pending toast by operation ID
 */
export function dismissPendingToast(operationId: string): void {
  const pendingToast = queue.find((t) =>
    t.element.textContent?.includes(operationId) ||
    t.id === operationId
  );
  if (pendingToast) {
    dismissToast(pendingToast.id);
  }
}

/* ── Legacy Compatibility ────────────────────────────── */

/**
 * Show an activity toast (legacy API from toasts.ts)
 * @deprecated Use showToast() instead
 */
export function showActivityToast(
  userName: string,
  actionType: string,
  replyToName?: string,
  pointsDelta?: number,
  _theme?: unknown,
): void {
  const dedupeKey = `${userName}:${actionType}`;
  
  let message = replyToName
    ? `${userName} & ${replyToName} worked together!`
    : `${userName} ${actionType} the garden`;
  
  if (pointsDelta && pointsDelta > 0) {
    message += ` • +${formatPoints(pointsDelta)} pts`;
  }
  
  showToast({
    message,
    variant: 'info',
    duration: 3000,
    dedupeKey,
    dedupeWindow: 2500,
    icon: actionType === 'bloom' ? 'flower' : actionType === 'decorate' ? 'sparkles' : 'sprout-small',
  });
}

/* ── Live Activity Feed Toasts (mobile + desktop optimized) ───────── */

interface LiveActivityToast {
  id: string;
  el: HTMLDivElement;
  timeout: ReturnType<typeof setTimeout> | null;
}

interface LiveActivityAggregate {
  toastId: string;
  count: number;
  points: number;
  lastUpdated: number;
  userName: string;
  actionType: string;
  replyToName?: string;
}

let liveActivityContainer: HTMLDivElement | null = null;
const liveActivityQueue: LiveActivityToast[] = [];
const liveActivityAggregates = new Map<string, LiveActivityAggregate>();
let liveToastCounter = 0;

const LIVE_ACTIVITY_MAX_VISIBLE = 5;
const LIVE_ACTIVITY_DURATION_MS = 4500;
const LIVE_ACTIVITY_AGGREGATE_WINDOW_MS = 1800;
let liveActivityPositionListenersBound = false;

function ensureLiveActivityContainer(): HTMLDivElement {
  if (liveActivityContainer && document.body.contains(liveActivityContainer)) return liveActivityContainer;

  liveActivityContainer = document.createElement('div');
  liveActivityContainer.id = 'live-activity-toast-container';
  liveActivityContainer.setAttribute('role', 'region');
  liveActivityContainer.setAttribute('aria-label', 'Live garden activity');
  liveActivityContainer.setAttribute('aria-live', 'polite');
  liveActivityContainer.style.cssText = `
    position: fixed;
    top: calc(var(--hud-inset, 8px) + env(safe-area-inset-top, 0px) + 44px);
    right: calc(var(--hud-inset, 8px) + env(safe-area-inset-right, 0px));
    z-index: var(--z-toast, 500);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    width: min(360px, calc(100vw - 24px));
    max-width: min(360px, calc(100vw - 24px));
    pointer-events: none;
  `;

  if (!document.getElementById('live-activity-toast-style')) {
    const style = document.createElement('style');
    style.id = 'live-activity-toast-style';
    style.textContent = `
      @media (max-width: 768px) {
        #live-activity-toast-container {
          top: calc(var(--hud-inset-mobile, 8px) + env(safe-area-inset-top, 0px) + 56px);
          left: 12px;
          right: 12px;
          width: auto;
          max-width: none;
          align-items: stretch;
        }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(liveActivityContainer);
  positionLiveActivityContainer();
  if (!liveActivityPositionListenersBound) {
    const onReposition = () => positionLiveActivityContainer();
    window.addEventListener('resize', onReposition);
    window.addEventListener('orientationchange', onReposition);
    window.addEventListener('garden-social-open', onReposition as EventListener);
    window.addEventListener('garden-social-close', onReposition as EventListener);
    liveActivityPositionListenersBound = true;
  }
  return liveActivityContainer;
}

function positionLiveActivityContainer(): void {
  if (!liveActivityContainer) return;
  const hud = document.getElementById('hud');
  const status = document.getElementById('status-cluster');
  const hudBottom = hud ? Math.ceil(hud.getBoundingClientRect().bottom) : 0;
  const statusBottom = status ? Math.ceil(status.getBoundingClientRect().bottom) : 0;
  const baselineTop = Math.max(hudBottom, statusBottom) + 8;
  liveActivityContainer.style.top = `${baselineTop}px`;
}

function formatLiveActivityMessage(entry: LiveActivityAggregate): string {
  let verb = 'updated';
  if (entry.actionType === 'message') verb = 'sent a message';
  else if (entry.actionType === 'reply') verb = entry.replyToName ? `replied to ${entry.replyToName}` : 'replied';
  else if (entry.actionType === 'reaction') verb = 'reacted';
  else if (entry.actionType === 'bloom') verb = 'bloomed';
  else if (entry.actionType === 'decorate') verb = 'decorated';

  const countPart = entry.count > 1 ? ` x${entry.count}` : '';
  const pointsPart = entry.points > 0 ? ` • +${formatPoints(entry.points)} pts` : '';
  return `${entry.userName} ${verb}${countPart}${pointsPart}`;
}

function removeLiveActivityToast(toastId: string): void {
  const idx = liveActivityQueue.findIndex((t) => t.id === toastId);
  if (idx === -1) return;
  const toast = liveActivityQueue[idx]!;
  liveActivityQueue.splice(idx, 1);
  if (toast.timeout) clearTimeout(toast.timeout);
  toast.el.style.opacity = '0';
  toast.el.style.transform = 'translateY(-6px)';
  setTimeout(() => toast.el.remove(), 220);
}

export function showLiveActivityToast(
  userName: string,
  actionType: string,
  replyToName?: string,
  pointsDelta?: number,
): void {
  const now = Date.now();
  positionLiveActivityContainer();
  const key = `${userName}:${actionType}:${replyToName ?? ''}`;
  const existing = liveActivityAggregates.get(key);

  if (existing && (now - existing.lastUpdated) <= LIVE_ACTIVITY_AGGREGATE_WINDOW_MS) {
    existing.count += 1;
    existing.points += Math.max(0, Number(pointsDelta ?? 0));
    existing.lastUpdated = now;

    const toast = liveActivityQueue.find((t) => t.id === existing.toastId);
    if (toast) {
      const msgEl = toast.el.querySelector('.live-activity-toast-message');
      if (msgEl) msgEl.textContent = formatLiveActivityMessage(existing);
      if (toast.timeout) clearTimeout(toast.timeout);
      toast.timeout = setTimeout(() => removeLiveActivityToast(toast.id), LIVE_ACTIVITY_DURATION_MS);
      return;
    }
  }

  const container = ensureLiveActivityContainer();
  const id = `live-activity-${++liveToastCounter}`;

  const entry: LiveActivityAggregate = {
    toastId: id,
    count: 1,
    points: Math.max(0, Number(pointsDelta ?? 0)),
    lastUpdated: now,
    userName,
    actionType,
    replyToName,
  };
  liveActivityAggregates.set(key, entry);

  const toast = document.createElement('div');
  toast.id = id;
  toast.style.cssText = `
    pointer-events: auto;
    background: rgba(12, 18, 26, 0.92);
    color: #e6edf3;
    border: 1px solid rgba(56, 189, 248, 0.28);
    border-left: 3px solid rgba(56, 189, 248, 0.9);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    padding: 9px 11px;
    width: 100%;
    min-height: 34px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.35;
    display: flex;
    align-items: center;
    gap: 8px;
    opacity: 0;
    transform: translateY(8px);
    transition: opacity 0.2s ease, transform 0.2s ease;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  `;

  const iconName = actionType === 'bloom' ? 'flower'
    : actionType === 'decorate' ? 'sparkles'
    : actionType === 'reaction' ? 'thumbsup'
    : actionType === 'reply' ? 'chat'
    : 'sprout-small';
  const iconWrap = document.createElement('span');
  iconWrap.innerHTML = gardenIcon(iconName, 14, 'display:block;opacity:0.95;');
  iconWrap.style.cssText = 'flex:0 0 auto;display:flex;align-items:center;';
  toast.appendChild(iconWrap);

  const msg = document.createElement('span');
  msg.className = 'live-activity-toast-message';
  msg.textContent = formatLiveActivityMessage(entry);
  msg.style.cssText = 'min-width:0;overflow-wrap:anywhere;';
  toast.appendChild(msg);

  container.appendChild(toast);
  requestAnimationFrame(() => {
    toast.classList.add('toast--visible');
  });

  const liveToast: LiveActivityToast = {
    id,
    el: toast,
    timeout: setTimeout(() => removeLiveActivityToast(id), LIVE_ACTIVITY_DURATION_MS),
  };
  liveActivityQueue.push(liveToast);

  // Pause auto-dismiss on hover/touch focus for readability.
  let remaining = LIVE_ACTIVITY_DURATION_MS;
  let startedAt = Date.now();
  const pause = () => {
    const t = liveActivityQueue.find((x) => x.id === id);
    if (!t) return;
    if (t.timeout) clearTimeout(t.timeout);
    remaining = Math.max(800, remaining - (Date.now() - startedAt));
    t.timeout = null;
  };
  const resume = () => {
    const t = liveActivityQueue.find((x) => x.id === id);
    if (!t || t.timeout) return;
    startedAt = Date.now();
    t.timeout = setTimeout(() => removeLiveActivityToast(id), remaining);
  };
  toast.addEventListener('mouseenter', pause);
  toast.addEventListener('mouseleave', resume);
  toast.addEventListener('touchstart', pause, { passive: true });
  toast.addEventListener('touchend', resume, { passive: true });

  const maxVisible = window.innerWidth <= 768 ? 1 : LIVE_ACTIVITY_MAX_VISIBLE;
  while (liveActivityQueue.length > maxVisible) {
    const oldest = liveActivityQueue.shift();
    if (oldest) removeLiveActivityToast(oldest.id);
  }

  setTimeout(() => {
    const active = liveActivityAggregates.get(key);
    if (active && active.toastId === id) {
      liveActivityAggregates.delete(key);
    }
  }, LIVE_ACTIVITY_DURATION_MS + LIVE_ACTIVITY_AGGREGATE_WINDOW_MS);
}

/**
 * Clean up toast container (legacy API)
 * @deprecated Use destroyToastManager() instead
 */
export function destroyToastContainer(): void {
  destroyToastManager();
}

/* ── Save Operation Toast API (legacy compatibility) ─── */

let saveToastId: string | null = null;

/** Show initial "Saving..." toast */
export function showSavingToast(): void {
  hideSaveToast(); // Clear any existing
  const id = `toast-${++toastIdCounter}`;
  saveToastId = id;
  
  const toast = createToastElement({
    message: 'Saving...',
    variant: 'info',
    loading: true,
    duration: 0,
  });
  toast.id = id;
  
  const wrapper = ensureContainer();
  wrapper.appendChild(toast);
  
  queue.push({
    id,
    element: toast,
    timeout: null,
    createdAt: Date.now(),
  });
  
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });
}

/** Show retry progress toast */
export function showRetryToast(attempt: number, maxRetries: number): void {
  if (saveToastId) {
    updateToast(saveToastId, {
      message: `Retrying (${attempt}/${maxRetries})...`,
      variant: 'warning',
      loading: true,
    });
  } else {
    showSavingToast();
    updateToast(saveToastId!, {
      message: `Retrying (${attempt}/${maxRetries})...`,
    });
  }
}

/** Show success toast - auto dismisses */
export function showSaveSuccessToast(): void {
  hideSaveToast();
  showToast({
    message: 'Saved!',
    variant: 'success',
    duration: 2000,
  });
}

/** Show final failure toast with manual retry button */
export function showSaveFailedToast(onRetry: () => void): void {
  hideSaveToast();
  showToast({
    message: 'Save failed',
    variant: 'error',
    duration: 0,
    action: {
      label: 'Retry',
      onClick: onRetry,
    },
  });
}

/** Hide and cleanup the save toast */
export function hideSaveToast(): void {
  if (saveToastId) {
    dismissToast(saveToastId);
    saveToastId = null;
  }
}

/** Cleanup all save toast resources */
export function destroySaveToast(): void {
  hideSaveToast();
}

/* ── Cleanup ─────────────────────────────────────────── */

/**
 * Destroy the toast manager and clean up all resources
 */
export function destroyToastManager(): void {
  dismissAllToasts();
  
  if (container && container.parentNode) {
    container.remove();
  }
  container = null;

  while (liveActivityQueue.length > 0) {
    const live = liveActivityQueue.shift();
    if (live?.timeout) clearTimeout(live.timeout);
    live?.el.remove();
  }
  liveActivityAggregates.clear();
  if (liveActivityContainer && liveActivityContainer.parentNode) {
    liveActivityContainer.remove();
  }
  liveActivityContainer = null;
  
  // Clear dedupe cache
  dedupeCache.length = 0;
  saveToastId = null;
}

/* ── Export Default ──────────────────────────────────── */

export default {
  show: showToast,
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  loading: showLoading,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
  update: updateToast,
  destroy: destroyToastManager,
  // GARDEN-138: Optimistic update notifications
  showRollbackToast,
  showPendingToast,
  dismissPendingToast,
  // Legacy APIs
  showActivityToast,
  showLiveActivityToast,
  destroyToastContainer,
  showSavingToast,
  showRetryToast,
  showSaveSuccessToast,
  showSaveFailedToast,
  hideSaveToast,
  destroySaveToast,
};
